"use client"
import * as React from "react"

import {
    ColumnDef,
    flexRender,
    ColumnFiltersState,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    VisibilityState,
} from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { useLocation, useNavigate } from "react-router-dom"
import { exportBillingsToExcel } from "@/utils/ExcelDataExport"
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline"
import { errorNotification } from "@/utils/Notification"
import { fetchAllBillings } from "@/apiCalls/apiCalls"
import useAxiosPrivate from "@/context/UseAxiosPrivate"

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
}

export function BillinDataTable<TData, TValue>({
    columns,
    data
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
        created_at: false,
        updated_at: false,
    })
    const [rowSelection, setRowSelection] = React.useState({})
    const [filteredData, setFilteredData] = React.useState<TData[]>(data)

    const navigate = useNavigate();
    const location = useLocation();
    const axiosPrivate = useAxiosPrivate();


    const getAllBillings = () => {
        let isMounted = true;
        const controller = new AbortController()
        axiosPrivate.get('/api/v1/billings/AllBillings', {
            signal: controller.signal
        }).then((response: any) =>
            response.data.data
        ).then((data: any) => {
            if (isMounted) {
                exportBillingsToExcel(data, 'billings')
            }
        }).catch((err: any) => {
            const errors = err?.response?.data?.errors;
            if (errors && errors?.[0]?.message === "token as expired") {
                let errror = errors?.[0]?.message
                errorNotification(
                    "There was an issue",
                    `${errror}`,
                    'topRight'
                );
                navigate('/', { state: { from: location }, replace: true });
            } else if (errors && Array.isArray(errors) && errors.length > 0) {
                errors.forEach((error: any) => {
                    const errorMessage = error.message || "An unknown error occurred";
                    errorNotification(
                        "There was an issue",
                        `${errorMessage}`,
                        'topRight'
                    );
                });
            } else {
                errorNotification(
                    "There was an issue",
                    "No server reponse",
                    'topRight'
                );
                navigate('/', { state: { from: location }, replace: true });
            }
        })
        return () => {
            isMounted = false
            controller.abort()
        };
    };

    const table = useReactTable({
        data: filteredData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        initialState: {
            columnVisibility: {
                Created_at: false,
                Updated_at: false,
            },
        },
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    })
    return (
        <>
            <div className="flex items-center justify-between py-1 ">
                <div>


                    <Button
                        variant="outline"
                        className="ml-auto flex items-center space-x-2"
                        onClick={getAllBillings}
                    >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                        <span>Export</span>
                    </Button>
                </div>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="ml-auto">
                            Columns
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {table
                            .getAllColumns()
                            .filter((column) => column.getCanHide()) // Columns that are toggleable
                            .map((column) => {
                                return (
                                    <DropdownMenuCheckboxItem
                                        key={column.id}
                                        className="capitalize"
                                        checked={column.getIsVisible()} // Reflect visibility state
                                        onCheckedChange={(value) => column.toggleVisibility(!!value)} // Toggle visibility
                                    >
                                        {column.id}
                                    </DropdownMenuCheckboxItem>
                                );
                            })}
                    </DropdownMenuContent>
                </DropdownMenu>

            </div>
            <div className="rounded-md border  border-black">
                <Table className="min-w-full divide-y divide-black p-3 ">
                    <TableHeader className="">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="divide-x divide-black">
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="px-6 py-1 border-b border-black  text-left text-sm font-medium text-black  tracking-wider"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody className=" divide-y divide-black ">
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                    className="divide-x divide-black"
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className="px-6 py-1 whitespace-nowrap border-b border-gray-700 text-black"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="px-6 py-4 whitespace-nowrap text-center border-b border-gray-700 text-gray-300"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected.
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    Previous
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    Next
                </Button>
            </div>

        </>
    )
}
